<template>
    <div>
        <div class="container-fluid">

            <!-- Header -->
            <div class="d-flex align-items-center justify-content-between pt-5 mb-4">
                <div class="d-block">
                    <div class="h1-lg display-tektur m-0">
                        Import Data {{ isStudent ? 'Siswa' : 'alumni' }}
                    </div>
                </div>

                <!-- Download Template Button & Major List / Back Button -->
                <div class="d-flex gap-2">
                    <button
                        v-if="isStudent"
                        type="button"
                        class="btn btn-outline-success"
                        @click="downloadStudentTemplate"
                    >
                        <i class="fa-solid fa-download fa-lg me-3"></i> Download Template
                    </button>
                    <a
                        v-else
                        :href="templateUrl"
                        class="btn btn-outline-success"
                        download
                    >
                        <i class="fa-solid fa-download fa-lg me-3"></i> Download Template
                    </a>
                    <router-link
                        v-if="!isStudent"
                        :to="{ name: 'major_code_reference' }"
                        target="_blank"
                        class="btn btn-outline-info"
                    >
                        <i class="fa-solid fa-list fa-lg me-2"></i> Lihat Jurusan
                    </router-link>
                    <button
                        v-else
                        class="btn btn-outline-secondary"
                        @click="goBack()"
                    >
                        <i class="fa-solid fa-arrow-left fa-lg me-2"></i> Kembali
                    </button>
                </div>
                
            </div>

            <!-- Upload Section -->
            <div class="card shadow-sm mb-4">
                <div class="card-body">
                    <div class="row align-items-end">
                        <div class="col-md-8">
                            <label class="form-label fw-bold">Upload File</label>
                            <input 
                                type="file" 
                                class="form-control"
                                accept=".xlsx,.xls,.csv"
                                @change="handleFileUpload"
                            />
                        </div>

                        <div class="col-md-4 mt-md-0">
                            <button 
                                class="btn btn-primary w-100"
                                :disabled="!file || fetch_process_file"
                                @click="processFile"
                            >
                                <span v-if="fetch_process_file" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {{ fetch_process_file ? 'Memproses...' : 'Proses File' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Preview Table -->
             <div v-if="fetch_process_file" class="d-flex justify-content-center my-5">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
             </div>

            <div v-if="(previewData.length > 0 && !fetch_process_file)" class="card shadow-sm">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span class="fw-bold">Preview Data</span>
                    <button class="btn btn-success btn-sm" @click="submitImport" v-if="(!fetch_process_file && previewData.length)">
                        Import Sekarang
                    </button>
                </div>
                <div class="card-body table-responsive">
                    <table class="table table-bordered table-hover">
                        <thead class="table-light">
                            <tr>
                                <th v-for="(col, index) in columns" :key="index" :style="{ width: col === 'No' ? '5%' : 'auto' }">
                                    {{ col }}
                                </th>
                                <!-- Kolom baru -->
                                <th>Keterangan</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in previewData" :key="rowIndex">
                                <td v-for="(col, colIndex) in columns" :key="colIndex" style="position: relative;">

                                    <!-- Password column -->
                                    <template v-if="col === 'Password'">
                                        <span>{{ showPassword[rowIndex] ? row[col] : '●●●●●' }}</span>
                                        <button 
                                            type="button" 
                                            class="btn btn-sm btn-outline-secondary"
                                            @click="togglePassword(rowIndex)" 
                                            style="margin-left:5px; padding: 2px 5px; font-size: 12px;"
                                        >
                                            {{ showPassword[rowIndex] ? 'Hide' : 'Show' }}
                                        </button>
                                    </template>

                                    <!-- Username column: highlight jika sudah ada -->
                                    <template v-else-if="col === 'Username'">
                                        <span :class="{'text-danger fw-bold': row.is_username_exist}">
                                            {{ row[col] }}
                                        </span>
                                    </template>

                                    <!-- Kode Jurusan column: tampilkan dengan universitas dan jurusan -->
                                    <template v-else-if="col === 'Kode Jurusan'">
                                        <div>
                                            <strong>{{ row[col] }}</strong>
                                            <div v-if="row.university_name" class="text-muted small mt-2">{{ row.university_name }}</div>
                                            <div v-if="row.major_name" class="text-muted small">{{ row.major_name }}</div>
                                            <div v-if="!row.university_name && !row.major_name" class="text-muted small">Kode jurusan tidak dikenali</div>
                                        </div>
                                    </template>

                                    <!-- Other columns -->
                                    <template v-else>
                                        {{ row[col] }}
                                    </template>

                                </td>

                                <!-- Kolom Keterangan -->
                                <td v-if="$route.params.type === 'student'">
                                    <span v-if="row.is_username_exist" class="text-danger fw-bold">
                                        Username sudah terpakai
                                    </span>
                                    <span v-else-if="!row.is_tahun_lulus_valid" class="text-danger fw-bold">
                                        Tahun lulus tidak valid
                                    </span>
                                    <span v-else-if="columns.some(c => !row[c] || row[c].toString().trim() === '')" class="text-warning fw-bold">
                                        Ada kolom kosong
                                    </span>
                                    <span v-else class="text-success">
                                        OK
                                    </span>
                                </td>

                                <td v-if="$route.params.type === 'alumni'">
                                    <span v-if="!row.is_tipe_valid" class="text-danger fw-bold">
                                        Tipe tidak valid
                                    </span>
                                    <span v-else-if="!row.major_name" class="text-danger fw-bold">
                                        Kode jurusan tidak dikenali
                                    </span>
                                    <span v-else class="text-success">
                                        OK
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Ketentuan Import -->
            <div class="card border-warning mb-4">
                <div class="card-header bg-warning text-dark fw-bold">
                    Ketentuan Import
                </div>
                <div class="card-body" v-if="$route.params.type === 'student'">
                    <ul class="mb-0">
                        <li>Gunakan template yang telah disediakan.</li>
                        <li>Kolom username, password, nama dan tahun lulus tidak boleh kosong.</li>
                        <li>Kolom tahun lulus diisi 4 digit angka tahun, contoh <code>2026</code>.</li>
                        <li>Username harus unik.</li>
                        <li>Maksimal ukuran file 2MB.</li>
                        <li>Maksimal data yang dapat diimpor adalah 30 baris.</li>
                        <li>Pastikan data sudah benar sebelum mengimpor.</li>                        
                    </ul>
                </div>
                <div class="card-body" v-if="$route.params.type === 'alumni'">
                    <ul class="mb-0">
                        <li>Gunakan template yang telah disediakan.</li>
                        <li>Semua kolom harus diisi.</li>
                        <li>Kolom tipe hanya dapat diisi dengan <code>rapor</code> atau <code>utbk</code>.</li>
                        <li>Maksimal ukuran file 2MB.</li>
                        <li>Maksimal data yang dapat diimpor adalah 30 baris.</li>
                        <li>Untuk kolom kode jurusan, silakan masukkan sesuai dengan kode jurusan yang tersedia. <router-link :to="{ name: 'major_code_reference' }" target="_blank">Lihat daftar jurusan dan kodenya</router-link></li>
                        <li>Pastikan data sudah benar sebelum mengimpor.</li>
                    </ul>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            file: null,
            previewData: [],
            columns: [],
            showPassword: {},
            waiting: false,
            fetch_process_file: false
        }
    },
    computed: {
        isStudent() {
            return this.$route.params.type === 'student'
        },
        templateUrl() {
            return 'https://scolacdn.com/formats/format-import-data-alumni.xlsx';
        }
    },
    methods: {
        // Template siswa dibuat di browser supaya header selalu sama dengan validasi backend.
        async downloadStudentTemplate() {
            const XLSX = await import('xlsx');
            let year = new Date().getFullYear();
            let rows = [
                ['No', 'Username', 'Password', 'NIS', 'Nama', 'Kelas', 'Tahun Lulus'],
                // Contoh data, sama seperti template lama ditambah tahun lulus.
                [1, 'username-1', 'abcd1234', 123123, 'Nama Lengkap 1', 'Kelas A', year],
                [2, 'username-2', 'abcd1235', 321321, 'Nama Lengkap 2', 'Kelas A', year],
                [3, 'username-3', 'abcd1236', 111222, 'Nama Lengkap 3', 'Kelas B', year],
                [4, 'username-4', 'abcd1237', 222111, 'Nama Lengkap 4', 'Kelas B', year + 1],
                [5, 'username-5', 'abcd1238', 332211, 'Nama Lengkap 5', 'Kelas C', year + 1]
            ];

            let worksheet = XLSX.utils.aoa_to_sheet(rows);
            worksheet['!cols'] = [{ wch: 6 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 35 }, { wch: 12 }, { wch: 14 }];

            let workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Data Siswa');
            XLSX.writeFile(workbook, 'format-import-data-siswa.xlsx');
        },

        togglePassword(rowIndex) {
            this.$set(this.showPassword, rowIndex, !this.showPassword[rowIndex]);
        },

        handleFileUpload(event) {
            this.file = event.target.files[0]
        },

        processFile() {
            if (!this.file) {
                this.$swal({
                    icon: 'warning',
                    title: 'Ups!',
                    text: 'Please select a file to upload.',
                });
                return;
            }

            if (this.isStudent) {
                this.processFileStudent();
            } else {
                this.processFileAlumni();
            }
        },

        processFileStudent() {
            let formData = new FormData();
            this.fetch_process_file = true;
            formData.append('file', this.file);
            formData.append('type', this.$route.params.type);

            this.$http.post(process.env.VUE_APP_APIHOST + 'users/import-preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(response => {
                if (response.data.status) {
                    this.columns = response.data.columns;
                    this.previewData = response.data.data.map(row => {
                        return {
                            No: row.no,
                            Username: row.username,
                            Password: row.password,
                            NIS: row.nis,
                            Nama: row.nama,
                            Kelas: row.kelas,
                            "Tahun Lulus": row.tahun_lulus,
                            is_username_exist: row.is_username_exist,
                            is_tahun_lulus_valid: row.is_tahun_lulus_valid
                        }
                    });
                    this.showPassword = {};
                } else {
                    this.$swal({
                        icon: 'warning',
                        title: 'Ups!',
                        text: response.data.message,
                    });
                }

                this.fetch_process_file = false;
            })
            .catch((error) => {
                this.$swal({
                    icon: 'warning',
                    title: 'Ups!',
                    text: 'Please check your internet connection.',
                });
                console.log(error);
                return;
            });
        },

        processFileAlumni() {
            let formData = new FormData();
            this.fetch_process_file = true;
            formData.append('file', this.file);
            formData.append('type', this.$route.params.type);

            this.$http.post(process.env.VUE_APP_APIHOST + 'users/import-preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(response => {
                if (response.data.status) {
                    this.columns = response.data.columns;
                    this.previewData = response.data.data.map(row => {
                        return {
                            No: row.no,
                            Nama: row.nama,
                            Tipe: row.tipe,
                            "Kode Jurusan": row.kode_jurusan,
                            "Tahun Lulus": row.tahun_lulus,
                            Nilai: row.nilai,
                            is_tipe_valid: row.is_tipe_valid,
                            major_name: row.major_name,
                            university_name: row.university_name
                        }
                    });
                } else {
                    this.$swal({
                        icon: 'warning',
                        title: 'Ups!',
                        text: response.data.message,
                    });
                }

                this.fetch_process_file = false;
            })
            .catch((error) => {
                this.$swal({
                    icon: 'warning',
                    title: 'Ups!',
                    text: 'Please check your internet connection.',
                });
                console.log(error);
                return;
            });
        },

        goBack() {
            if(this.isStudent) {
                this.$router.push({ name: 'student' });
            } else {
                this.$router.push({ name: 'school_pg_alumni' });
            }
        },
        submitImport() {
            let total_data = 0;
            
            if (this.isStudent) {
                for (let row of this.previewData) {
                    if (row.is_username_exist || !row.is_tahun_lulus_valid || this.columns.some(c => !row[c] || row[c].toString().trim() === '')) {
                        continue;
                    }
                    total_data++;
                }
            }

            if (!this.isStudent) {
                for (let row of this.previewData) {
                    if (!row.is_tipe_valid || !row.major_name || this.columns.some(c => !row[c] || row[c].toString().trim() === '')) {
                        continue;
                    }
                    total_data++;
                }
            }

            if (total_data === 0) {
                if (this.isStudent) {
                    this.$swal({
                        icon: 'warning',
                        title: 'Ups!',
                        text: 'Tidak ada data valid untuk diimpor. Pastikan semua data sudah benar dan username belum terpakai.',
                    });
                } else {
                    this.$swal({
                        icon: 'warning',
                        title: 'Ups!',
                        text: 'Tidak ada data valid untuk diimpor. Pastikan semua data sudah benar dan tipe hanya berisi "rapor" atau "utbk".',
                    });
                }
                return;
            }

            this.$swal.fire({
                title: 'Konfirmasi Import',
                text: `Anda akan mengimpor ${total_data} data ${this.isStudent ? 'siswa' : 'alumni'}. Pastikan data sudah benar. Lanjutkan?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Ya, impor sekarang!',
                cancelButtonText: 'Batal'
            }).then((result) => {

                if (result.isConfirmed) {

                    // 🔥 TAMPILKAN LOADING
                    this.$swal.fire({
                        title: 'Sedang Mengimpor...',
                        text: 'Mohon tunggu, data sedang diproses.',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    let formData = new FormData();
                    formData.append('file', this.file);
                    formData.append('type', this.$route.params.type);

                    this.$http.post(process.env.VUE_APP_APIHOST + 'users/import-data', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then(response => {

                        this.$swal.close(); // ✅ tutup loading dulu

                        if (response.data.status) {
                            this.$swal({
                                icon: 'success',
                                title: 'Berhasil!',
                                text: response.data.message,
                            });

                            this.previewData = [];
                            this.file = null;

                            if (this.isStudent) {
                                this.$router.push({ name: 'student' });
                            } else {
                                this.$router.push({ name: 'school_pg_alumni' });
                            }
                        } else {
                            this.$swal({
                                icon: 'warning',
                                title: 'Ups!',
                                text: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {

                        this.$swal.close(); // ✅ tutup loading kalau error

                        this.$swal({
                            icon: 'error',
                            title: 'Terjadi Kesalahan!',
                            text: 'Please check your internet connection.',
                        });

                        console.log(error);
                    });
                }
            });
        }

    }
}
</script>

<style scoped>
.card {
    border-radius: 12px;
}
</style>
